import gql from "graphql-tag";
import { treatmentFieldsFragment, treatmentWithArticle71IndicationsFieldsFragment } from "../../../../controllers/queries";
import { DOCUMENT_MESSAGE_FIELDS_FRAGMENT } from "../messaging/queries";

export const CREATE_INVOICE_FROM_REFUND_IDS = gql`
	mutation CreateDocument($input: CreateDocumentInput) {
		CreateDocument(request: $input)
	}
`;

export const ADD_REFUNDS_TO_EXISTING_DOCUMENT = gql`
	mutation AddRefundsToExistingDocument($input: CreateDocumentInput) {
		AddRefundsToExistingDocument(request: $input)
	}
`;

const DOCUMENT_FRAGMENT = gql`
	fragment InvoiceFragment on Document {
		id
		status
		createdByEmail
		creatorTenantId
		tenantIds
		counterParties
		createdOn
		issuedOn
		isDeleted
		creatorTenantId
		type
		invoiceAmount
		hasPatientTransactions
		hasCaselessTransactions
		uploadedVariantKey
		uploadedOn
		dunningDate
		beneficiary
		paymentReferenceLyfegen
		paymentReceiptPayer
	}
`;

const DOCUMENT_REFUND_GROUP_FRAGMENT = gql`
	fragment DocumentRefundGroupFragment on DocumentRefundGroup {
		id
		tenantIds
		case(where: { isDeleted: false }) {
			id
			rating
			agreementTag
			canUserViewFinancials
			actualAgreementId
			patientInsuranceId
			sourceTenantId
			flexEntitiesConnection(where: { edge: { isStartingPoint: true } }) {
				edges {
					node {
						type {
							id
						}
						data
						externalFormId
					}
				}
			}
			financialDataPoints(where: { isDeleted: false, isLast: true }) {
				uuid
				generatedUuid
				refundable
			}
		}
		agreement(where: { isCaseless: true }) {
			id
			name
			isCaseless
			currency {
				Code
				Name
			}
			tags {
				type
			}
			financialDataPoints(where: { isDeleted: false, isLast: true }) {
				uuid
				generatedUuid
				refundable
			}
		}
		threadMessageCount
		refunds {
			id
			isDeleted
			amount
			adjustedAmount
			adjustedBy {
				id
				firstName
				lastName
			}
			adjustedRate
			lastAdjustedAt
			adjustmentComment
			product
			sourceFinancialDataPoint {
				agreement {
					id
				}
			}
			documentFinancialDataPointConnection(where: { edge: { connectionId: $invoiceId } }) {
				edges {
					status
					smartMipStatus
					node {
						uuid
						generatedUuid
						quantity
						basePrice
						refundablePercentage
						payable
						variable
						refundable
						prescriptionDate
						isDelta
						treatment {
							Code
							Name
							country {
								name
							}
							regulatedBy {
								BrandName
								DescriptionDe
							}
						}
						evaluationSnapshot
						evaluates {
							tenantId
							rating
							invoiceNumber
							purchaseId
							insuranceClaimId
							externalFormId
							dateAttributeName
						}
					}
				}
			}
			agreementId
			predecessorIds
			canBeReplacedInDocumentIds
			canBeReplacementInDocumentIds
			successorIds
		}
	}
`;

export const GET_PDF_EXPORT_VALUES = gql`
	query GetPdfExportValues($invoiceId: ID!) {
		GetDocumentExportFieldDefaults(invoiceId: $invoiceId) {
			lastExportDate
			orderNo
			senderLegalName
			documentSenderDomicile
			documentSenderPhone
			documentSenderFax
			documentSenderContactFirstName
			documentSenderContactLastName
			documentSenderContactPhone
			documentSenderContactEmail
			documentReceiverLegalName
			documentReceiverBillingAddress
		}
		documents(where: { id: $invoiceId }) {
			type
			creatorTenantId
			product
			areExportValuesSaved
			orderNo
			senderLegalName
			documentSenderDomicile
			documentSenderPhone
			documentSenderFax
			documentSenderContactFirstName
			documentSenderContactLastName
			documentSenderContactPhone
			documentSenderContactEmail
			documentReceiverLegalName
			documentReceiverBillingAddress
		}
		GetDocumentCreatorOrganization(invoiceId: $invoiceId) {
			id
			legalName
			domicile
			billingAddress
			deliveryAddress
			phone
			fax
			contactFirstName
			contactLastName
			contactPhone
			contactEmail
		}
		GetDocumentCounterpartyOrganization(invoiceId: $invoiceId) {
			id
			legalName
			domicile
			billingAddress
			deliveryAddress
			phone
			fax
			contactFirstName
			contactLastName
			contactPhone
			contactEmail
		}
	}
`;

export const UPDATE_DOCUMENT_EXPORT_FIELDS = gql`
	mutation UpdateDocumentExportFields($input: DocumentExportFieldsInput!) {
		UpdateDocumentExportFields(input: $input)
	}
`;

export const GET_REVIEW_INVOICE_BY_ID = gql`
	${DOCUMENT_FRAGMENT}
	${DOCUMENT_REFUND_GROUP_FRAGMENT}
	${DOCUMENT_MESSAGE_FIELDS_FRAGMENT}

	query GetReviewInvoice($filter: DocumentWhere!, $invoiceId: ID!) {
		documents(where: $filter) {
			...InvoiceFragment
			agreementsWithoutUserAccess {
				id
				name
			}
			agreementsWithOnlyViewingFullAccess {
				id
				name
			}
			coveragePeriod
			periodDateType
			isAgreementBuyer
			isAgreementSeller
			isAgreementOwner
			isAssignee
			canAssignUser
			isCommentRequierd
			canUserDelete
			canConfirm
			canApprove
			currentReviewerOrganization
			invoiceAmount
			isConfirmedBySeller
			isConfirmedBySender
			isConfirmedByBuyer
			isConfirmedByExternalParty
			isSentForExternalReview
			isSentToSmartMip
			smartMipReimbursementId
			smartMipStatus
			settledOn
			beneficiary
			paymentReferenceLyfegen
			paymentReceiptPayer
			lastChangedOn
			lastRemindedOn
			paymentReminderEmail
			sentToReviewOn
			reviewRemindedOn
			agreementBuyerInvoiceInfo {
				dualApprovalThreshold
				name
			}
			agreementSellerInvoiceInfo {
				dualApprovalThreshold
				name
			}
			confirmedBy {
				id
				tenantId
			}
			approvedBy {
				id
				tenantId
			}
			rejectedBy {
				id
			}
			documentRefundGroups {
				...DocumentRefundGroupFragment
			}
			baseAgreements {
				id
				isFirstVersion
				initialVersionId
				name
				currency {
					Code
					Name
				}
				enrollingConditionSets {
					scopes {
						organizationScopesConnection {
							edges {
								organizationScopeId
							}
						}
						countryScopesConnection {
							edges {
								countryScopeId
							}
						}
					}
				}
				nextVersions {
					enrollingConditionSets {
						scopes {
							organizationScopesConnection {
								edges {
									organizationScopeId
								}
							}
							countryScopesConnection {
								edges {
									countryScopeId
								}
							}
						}
					}
				}
			}
			messages(where: { isDeleted: false }, options: { sort: { createdOn: ASC } }) {
				...DocumentMessageFields
			}
		}

		documentTableRows(where: { id: $invoiceId }) {
			agreementWithoutUserAccessNames
		}
	}
`;

export const GET_INVOICE_BY_ID = gql`
	${DOCUMENT_FRAGMENT}
	${DOCUMENT_REFUND_GROUP_FRAGMENT}
	${DOCUMENT_MESSAGE_FIELDS_FRAGMENT}

	query GetInvoice($invoiceId: ID!) {
		documents(where: { id: $invoiceId }) {
			...InvoiceFragment
			canUserDelete
			canAssignUser
			canBeSentToSmartMip
			agreementsWithOnlyViewingFullAccess {
				id
				name
			}
			coveragePeriod
			periodDateType
			agreementsWithoutUserAccess {
				id
				name
			}
			agreementSellerInvoiceInfo {
				name
				dualApprovalThreshold
			}
			agreementBuyerInvoiceInfo {
				name
				dualApprovalThreshold
			}
			isAgreementBuyer
			documentRefundGroups {
				...DocumentRefundGroupFragment
			}
			baseAgreements {
				id
				isFirstVersion
				initialVersionId
				name
				currency {
					Code
					Name
				}
				enrollingConditionSets {
					scopes {
						organizationScopesConnection {
							edges {
								organizationScopeId
							}
						}
						countryScopesConnection {
							edges {
								countryScopeId
							}
						}
					}
				}
				nextVersions {
					enrollingConditionSets {
						scopes {
							organizationScopesConnection {
								edges {
									organizationScopeId
								}
							}
							countryScopesConnection {
								edges {
									countryScopeId
								}
							}
						}
					}
				}
			}
			messages(where: { isDeleted: false }, options: { sort: { createdOn: ASC } }) {
				...DocumentMessageFields
			}
		}

		documentTableRows(where: { id: $invoiceId }) {
			agreementWithoutUserAccessNames
		}
	}
`;

export const GET_USER_EMAIL = gql`
	query SearchUserEmailByName($input: SearchUserEmailByNameInput!) {
		InvoiceCounterPartiesUsers(input: $input, limit: 10) {
			firstName
			lastName
			email
			tenantId
		}
	}
`;

export const GET_ORGANIZATION_NAME = gql`
	query GetOrgName($tenantId: String!) {
		OrganizationFromTenantId(tenantId: $tenantId) {
			name
			tenantId
		}
	}
`;

export const SAVE_DRAFT_INVOICE = gql`
	mutation SaveDraftInvoice($input: SaveDraftInvoiceInput) {
		SaveDraftInvoice(request: $input)
	}
`;

export const SAVE_NEW_INVOICE_FOR_REVIEW = gql`
	mutation SaveNewInvoiceForReview($input: UpdateInvoiceForReviewInput) {
		SendInvoiceForReview(request: $input)
	}
`;

export const SAVE_INVOICE_FOR_INTERNAL_REVIEW = gql`
	mutation SaveInvoiceForInternalReview($input: SaveInvoiceForInternalReviewInput) {
		SaveInvoiceForInternalReview(request: $input)
	}
`;

export const RESEND_INVOICE_FOR_REVIEW = gql`
	mutation SendInvoiceForReReview($input: ResendInvoiceForReviewInput) {
		ResendInvoiceForReview(request: $input)
	}
`;

export const GET_EXTERNAL_EXPOSURES = gql`
	query GetExternalExposures {
		GetExternalExposures {
			receiverEmail
			userId
			organizationId
			organizationName
			resourceType
			subType
			creatorEmail
			creatorName
			useCC
			id
			exposedOn
			lifetimeSeconds
		}
	}
`;

export const GET_DEFAULT_RECEIVER = gql`
	query GetDefaultReceiver($documentId: ID!, $notificationId: Int!) {
		GetDefaultReceiver(documentId: $documentId, notificationId: $notificationId)
	}
`;

export const GET_EXTERNAL_EXPOSURES_BY_DOCUMENT_ID = gql`
	query GetExternalExposureByDocumentId($documentId: String!) {
		GetExternalExposuresByDocumentId(documentId: $documentId) {
			receiverEmail
			organizationId
			organizationName
			resourceType
			subType
			creatorEmail
			creatorName
			useCC
			id
			exposedOn
			lifetimeSeconds
		}
	}
`;

export const GET_ORG_NAME_FROM_TENANT_ID = gql`
	query GetOrgNameFromTenantId($tenantId: String!) {
		OrganizationFromTenantId(tenantId: $tenantId) {
			name
		}
	}
`;

export const SEND_DOCUMENT_FOR_EXTERNAL_REVIEW = gql`
	mutation SaveDocumentForExternalReview($input: SendDocumentForExternalReviewInput!) {
		SendDocumentForExternalReview(request: $input)
	}
`;

export const SEND_PAYMENT_REMINDER = gql`
	mutation SendPaymentReminder($input: SendDocumentForExternalReviewInput!) {
		SendPaymentReminder(request: $input)
	}
`;

export const GET_ASSIGNEE_AND_ORG_NAME = gql`
	query GetAssigneeAndOrgName($tenantId: String!, $invoiceId: ID!) {
		OrganizationFromTenantId(tenantId: $tenantId) {
			name
			tenantId
		}
		GetAssignee(invoiceId: $invoiceId, tenantId: $tenantId) {
			email
			firstName
			lastName
		}
		GetAgreementIdFromInvoiceId(invoiceId: $invoiceId)
	}
`;

export const SAVE_REVIEWED_INVOICE = gql`
	mutation SaveReviewedInvoice($input: SaveReviewedInvoiceInput) {
		SaveReviewedInvoice(request: $input)
	}
`;

export const ISSUE_INVOICE = gql`
	mutation IssueInvoice($input: SaveReviewedInvoiceInput) {
		IssueInvoice(request: $input)
	}
`;

export const APPROVE_DOCUMENT = gql`
	mutation ApproveDocument($documentId: ID!) {
		ApproveDocument(documentId: $documentId)
	}
`;

export const CONFIRM_INVOICE = gql`
	mutation ConfirmInvoice($id: ID!) {
		ConfirmInvoice(id: $id)
	}
`;

export const REJECT_INVOICE = gql`
	mutation RejectInvoice($id: ID!) {
		RejectInvoice(id: $id)
	}
`;

export const ISSUE_INVOICE_WITHOUT_REVIEW = gql`
	mutation IssueInvoiceWithoutReview($input: IssueInvoiceWithoutReviewInput) {
		IssueInvoiceWithoutReview(request: $input)
	}
`;

export const RECALL_TO_DRAFT = gql`
	mutation RecallToDraft($input: RecallToDraftInput) {
		RecallToDraft(request: $input)
	}
`;

export const DELETE_INVOICE = gql`
	mutation DeleteInvoice($input: DeleteInvoiceInput) {
		DeleteInvoice(request: $input)
	}
`;

export const GET_REFUND_INVOICE_DATA = gql`
	${treatmentFieldsFragment}

	query RefundInvoice($invoiceId: ID!) {
		documents(where: { id: $invoiceId, isDeleted: false }) {
			agreementsWithoutUserAccess {
				id
				name
			}
			id
			invoiceAmount
			adjustedInvoiceAmount
			tags {
				id
				name
				type
			}
			formattedTags
			coveragePeriod
			type
			status
			issuedOn
			isAgreementSeller
			isAgreementBuyer
			agreementSellerInvoiceInfo {
				id
				name
				billingAddress
				deliveryAddress
				legalName
				domicile
				contactEmail
				contactPhone
				phone
				contactOccupation
				contactFirstName
				contactLastName
				fax
				logo
				dualApprovalThreshold
			}
			agreementBuyerInvoiceInfo {
				id
				name
				IBAN
				billingAddress
				deliveryAddress
				contactFirstName
				contactLastName
				contactOccupation
				contactPhone
				contactEmail
				domicile
				accountNumber
				BIC
				accountName
				paymentTerms
				eIN
				fax
				legalName
				logo
				phone
				website
				dualApprovalThreshold
			}
			refunds(where: { isDeleted: false }) {
				id
				agreementId
				sourceFinancialDataPoint {
					evaluates {
						tenantId
					}
				}
				case(where: { isDeleted: false }) {
					sourceTenantId
					actualAgreementId
					canUserViewFinancials
				}
				agreement(where: { isDeleted: false, isCaseless: true }) {
					id
					name
					isCaseless
					currency {
						Code
					}
				}
				product
				documentFinancialDataPointConnection(where: { edge: { connectionId: $invoiceId } }) {
					edges {
						status
						node {
							quantity
							refundablePercentage
							basePrice
							payable
							variable
							refundable
							prescriptionDate
							treatment {
								...TreatmentFields
							}
							evaluates {
								tenantId
							}
						}
					}
				}
				amount
				adjustedAmount
				adjustedRate
				status
			}
			documentRefundGroups {
				refunds(where: { isDeleted: false }) {
					sourceFinancialDataPoint {
						agreement {
							id
							initialVersionId
						}
					}
				}
			}
			baseAgreements {
				id
				initialVersionId
				name
				currency {
					Code
				}
			}
			createdBy {
				id
				firstName
				lastName
				position
				department
				signature
			}
		}
		IsDualApprovalSupportedForDocument(id: $invoiceId)
		GetFocDataFromInvoiceId(invoiceId: $invoiceId) {
			product
			discount
			foc
			focStatus {
				approved
				declined
			}
		}
	}
`;

const financialDataPointsFragment = gql`
	fragment DataPointFields on FlexFinancialDataPoint {
		uuid
		generatedUuid
		isLast
		isDelta
		evaluates {
			id
			type {
				id
			}
			data
			tenantId
		}
		basePrice
		basePriceType
		refundablePercentage
		refundable
		evaluationSnapshot
		documentStatus
		sourceDocument {
			id
			status
		}
		sourceRefund(where: { isDeleted: false }) {
			id
			status
			adjustedAmount
			adjustedRate
		}
	}
`;

export const SET_DUNNING_DATE = gql`
	mutation SetDunningDate($documentId: ID!, $dunningDate: Date) {
		SetDunningDate(documentId: $documentId, dunningDate: $dunningDate)
	}
`;

export const UPDATE_DOCUMENT_PAYMENT_FIELDS = gql`
	mutation UpdateDocumentPaymentFields($input: UpdateDocumentPaymentFieldsInput!) {
		UpdateDocumentPaymentFields(input: $input)
	}
`;

export const UPLOAD_TO_SFTP = gql`
	mutation UploadToSftp($documentId: ID!) {
		UploadToSftp(documentId: $documentId) {
			uploadedVariantKey
			uploadedOn
		}
	}
`;

export const GET_REFUND_XLS_DATA = gql`
	${treatmentWithArticle71IndicationsFieldsFragment}
	${financialDataPointsFragment}

	fragment RefundXlsBaseAgreementFieldsFragment on BaseAgreement {
		id
		initialVersionId
		formattedTags
		currency {
			Code
		}
		country {
			name
		}
		enrollingConditionSets {
			scopes {
				diseases {
					Icd10Code
					Name
				}
				indications {
					id
					code
					type
					intent
					line
					mutation
					typeLocalizationKey
					intentLocalizationKey
					lineLocalizationKey
					mutationLocalizationKey
				}
				organizationScopesConnection {
					edges {
						organizationScopeId
					}
				}
				countryScopesConnection {
					edges {
						countryScopeId
					}
				}
			}
		}
		scenarios {
			order
			conditions {
				order
				conditionType
				consideredRating
				article71Status
				patentStatus
			}
		}
		treatmentPricesConnection {
			edges {
				basePriceType
				node {
					Code
					GTIN
				}
			}
		}
	}

	query RefundXlsInvoice($invoiceId: ID!) {
		documents(where: { id: $invoiceId }) {
			id
			status
			type
			createdByEmail
			counterParties
			creatorOrgName
			createdOn
			issuedOn
			settledOn
			formattedTags
			coveragePeriodEnd
			hasCaselessTransactions
			hasPatientTransactions
			documentRefundGroups {
				agreement(where: { isDeleted: false, isCaseless: true }) {
					id
					isCaseless
					currency {
						Code
					}
					country {
						name
					}
					financialDataPoints(where: { isDeleted: false, isLast: true, isDelta: false }) {
						...DataPointFields
					}
				}
				refunds(where: { isDeleted: false }) {
					id
					caseId
					status
					agreementId
					purchaseOrPatientId
					sourceFinancialDataPoint {
						agreement {
							id
						}

						evaluates {
							tenantId
						}
					}
					adjustedAmount
					adjustedRate
					documentFinancialDataPointConnection {
						edges {
							connectionId
							status
							node {
								uuid
								generatedUuid
								prescriptionDate
								isLast
								isDelta
								refundable
								basePrice
								netPrices
								basePriceType
								refundablePercentage
								scenarioZeroNetPrices
								scenarioZeroRelevantNetPrices
								scenarioZeroMainNetPrices
								quantity
								sourceRefund {
									id
									adjustedAmount
									adjustedRate
								}
								evaluates {
									id
									data
									tenantId
									type {
										id
										name
									}
								}
								generatedFor {
									id
									sourceTenantId
									actualAgreementId
								}
								evaluationSnapshot
								treatment {
									...TreatmentWithArticle71IndicationsFields
								}
							}
						}
					}
				}
				case(where: { isDeleted: false }) {
					id
					sourceTenantId
					actualAgreementId

					disease {
						Icd10Code
						Name
					}

					indication {
						id
						code
						type
						intent
						line
						mutation
						typeLocalizationKey
						intentLocalizationKey
						lineLocalizationKey
						mutationLocalizationKey
					}

					flexEntities(where: { type: { isStartingPoint: true }, isDeleted: false }) {
						id
						data
						type {
							id
							name
						}
					}

					financialDataPoints(where: { isDeleted: false, isLast: true, isDelta: false }) {
						...DataPointFields
					}

					relevantArticle71Indication {
						name
						approvalDate
						sllAdditionDate
					}
				}
			}
			baseAgreements {
				...RefundXlsBaseAgreementFieldsFragment
				nextVersions {
					...RefundXlsBaseAgreementFieldsFragment
				}
			}
		}

		GetFocDataFromInvoiceId(invoiceId: $invoiceId) {
			product
			discount
			foc
			focStatus {
				approved
				declined
			}
		}
	}
`;

export const ASSIGN_INVOICE_TO_USER = gql`
	mutation AssignInvoiceToUser($input: AssignInvoiceToUserInput) {
		AssignInvoiceToUser(request: $input)
	}
`;

export const GET_AGREEMENT_ID_FROM_INVOICE_ID = gql`
	query GetAgreementIdFromInvoiceId($invoiceId: ID!) {
		GetAgreementIdFromInvoiceId(invoiceId: $invoiceId)
	}
`;

export const GET_APPROVER_SIGNATURES_AND_INFO = gql`
	query GetApproverSignaturesAndInfo($documentId: ID!) {
		GetApproverSignaturesAndInfo(documentId: $documentId) {
			id
			approverRole
			firstName
			lastName
			position
			department
			signature
		}
	}
`;

export const GET_FOC_DATA_FROM_INVOICE_ID = gql`
	query GetFocDataFromInvoiceId($invoiceId: ID!) {
		GetFocDataFromInvoiceId(invoiceId: $invoiceId) {
			product
			discount
			foc
			focStatus {
				approved
				declined
			}
		}
	}
`;

export const IS_DUAL_APPROVAL_SUPPORTED_FOR_DOCUMENT = gql`
	query IsDualApprovalSupportedForDocument($id: ID!) {
		IsDualApprovalSupportedForDocument(id: $id)
	}
`;

export const BOOK_DEMO = gql`
	mutation BookDemo($input: BookDemoInput) {
		BookDemo(request: $input)
	}
`;

export const SEND_DOCUMENT_TO_SMARTMIP = gql`
	mutation SendDocumentToSmartMip($input: SendDocumentToSmartMipInput!) {
		SendDocumentToSmartMip(input: $input) {
			smartMipReimbursementId
		}
	}
`;

export const SEND_DECISIONS_TO_SMARTMIP = gql`
	mutation SendDecisionsToSmartMip($input: SendDecisionsToSmartMipInput!) {
		SendDecisionsToSmartMip(input: $input)
	}
`;

export const DOWNLOAD_SMARTMIP_INVOICE_PDF = gql`
	query DownloadSmartMipInvoicePdf($input: DownloadSmartMipInvoicePdfInput!) {
		DownloadSmartMipInvoicePdf(input: $input) {
			fileName
			contentType
			content
		}
	}
`;

export const GET_SMARTMIP_INVOICES_AVAILABLE = gql`
	query GetSmartMipInvoicesAvailable($input: GetSmartMipInvoicesAvailableInput!) {
		GetSmartMipInvoicesAvailable(input: $input)
	}
`;

export const GET_DOCUMENT_STATUS = gql`
	query GetDocumentStatus($id: ID!) {
		documents(where: { id: $id }) {
			status
		}
		documentTableRows(where: { id: $id }) {
			status
		}
	}
`;

export const GET_DOCUMENT_XML_REPORT = gql`
	query GetDocumentXmlReport($documentId: ID!) {
		GetDocumentXmlReport(documentId: $documentId) {
			filename
			data
		}
	}
`;

export const GET_PDF_REPORT_FILE = gql`
	query GetPdfReportFile($documentId: ID!) {
		GetPdfReportFile(documentId: $documentId) {
			filename
			data
		}
	}
`;

export const GET_PDF_TOKEN = gql`
	query GetPdfToken($documentId: ID!) {
		GetPdfToken(documentId: $documentId) {
			token
		}
	}
`;
